/**
 * CloverWebhookActivity — live-updating panel showing the last few Clover
 * webhook events this merchant received. Once webhooks are hooked up in
 * the Clover Developer Dashboard, making a sale on the POS should show
 * up here within seconds — proving real-time sync is working without
 * making the merchant click "Sync Now".
 */
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Zap, RefreshCw, CheckCircle2, AlertCircle, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Human labels for the objectId prefix Clover sends.
const PREFIX_LABEL = {
  O: { label: "Order",     color: "bg-cyan-100 text-cyan-800 border border-cyan-300" },
  P: { label: "Payment",   color: "bg-emerald-100 text-emerald-800 border border-emerald-300" },
  I: { label: "Inventory", color: "bg-amber-100 text-amber-800 border border-amber-300" },
  M: { label: "Merchant",  color: "bg-slate-100 text-slate-700 border border-slate-300" },
  E: { label: "Employee",  color: "bg-slate-100 text-slate-700 border border-slate-300" },
  C: { label: "Customer",  color: "bg-slate-100 text-slate-700 border border-slate-300" },
};

function relTime(iso) {
  if (!iso) return "";
  const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 5) return "just now";
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function CloverWebhookActivity({ businessSlug, isDark }) {
  const [batches, setBatches] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/b/${businessSlug}/clover/webhooks/events?limit=15`);
      if (!res.ok) return;
      const data = await res.json();
      setConnected(!!data.connected);
      setBatches(Array.isArray(data.events) ? data.events : []);
    } finally {
      setLoading(false);
    }
  }, [businessSlug]);

  useEffect(() => {
    load();
    // Poll every 15s so the merchant sees events flow in near-real-time
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (!connected) return null;

  const cardBg = isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200";
  const sub    = isDark ? "text-slate-400" : "text-slate-500";
  const heading = isDark ? "text-white" : "text-slate-800";

  // Flatten batches → recent individual events (max 10 to keep it scannable)
  const rows = [];
  for (const b of batches) {
    for (const e of (b.events || [])) {
      rows.push({ ...e, received_at: b.received_at, processed_at: b.processed_at });
      if (rows.length >= 10) break;
    }
    if (rows.length >= 10) break;
  }

  return (
    <Card className={cardBg} data-testid="clover-webhook-activity-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className={`flex items-center gap-2 ${heading}`}>
            <Zap className="w-5 h-5 text-amber-500" />
            Real-Time Webhook Activity
            {rows.length > 0 && (
              <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-300 hover:bg-emerald-100">
                Live
              </Badge>
            )}
          </CardTitle>
          <Button
            onClick={load}
            disabled={loading}
            size="sm"
            variant="outline"
            data-testid="clover-webhook-activity-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="space-y-2">
            <p className={`text-sm ${sub}`}>
              No webhook events yet. To receive real-time sync from Clover:
            </p>
            <ol className={`text-xs list-decimal pl-5 space-y-0.5 ${sub}`}>
              <li>Open Clover Developer Dashboard &rarr; Your Apps &rarr; App Settings &rarr; Webhooks.</li>
              <li>Paste the webhook URL from the Diagnostics panel above.</li>
              <li>Enable events (Orders, Payments, Inventory).</li>
              <li>Click <em>Send Verification Code</em> — it will land here automatically.</li>
              <li>Copy the code back into Clover to activate.</li>
            </ol>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="clover-webhook-activity-list">
            {rows.map((e, i) => {
              const prefix = (e.object_id || "").split(":")[0];
              const meta = PREFIX_LABEL[prefix] || { label: prefix || "?", color: "bg-slate-100 text-slate-700 border border-slate-300" };
              const StatusIcon = e.status === "upserted" || e.status === "deleted" || e.ok
                ? CheckCircle2
                : e.status === "pending"
                  ? Clock
                  : AlertCircle;
              const iconColor = e.status === "upserted" || e.status === "deleted" || e.ok
                ? "text-emerald-500"
                : e.status === "pending"
                  ? "text-slate-400"
                  : "text-amber-500";
              return (
                <div
                  key={i}
                  className={`flex items-center justify-between text-xs p-2 rounded ${isDark ? "bg-slate-900/40" : "bg-slate-50"}`}
                  data-testid={`clover-webhook-event-${i}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <StatusIcon className={`w-4 h-4 flex-shrink-0 ${iconColor}`} />
                    <Badge className={`${meta.color} text-[10px]`}>{meta.label}</Badge>
                    <span className={`font-mono truncate ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                      {e.object_id}
                    </span>
                    <span className={sub}>· {e.type}</span>
                  </div>
                  <span className={`${sub} flex-shrink-0 ml-2`}>{relTime(e.received_at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
